import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import merubotLogo from '../../../assets/images/merubot_logo.png';
import './PricingDetail.css'; 
import Navbar from './PricingDetailNavbar';

/* ─── Plan Data ─────────────────────────────────────────────────────────── */
const PLANS = [
  {
    name: 'Starter',
    badge: 'Entry Level',
    color: 'blue',
    inr: '₹4,500',
    usd: '$75',
    credits: '4,500',
    tagline: 'Perfect for individuals exploring AI hiring and simplifying their recruitment process.',
    idealFor: ['Solo Recruiters', 'HR Generalists', 'Freelance Consultants', 'Early-stage Startups'],
    teamSize: 2,
    quotas: [
      { name: 'AI Interviews',      val: 'upto 10',    pct: 10  },
      { name: 'JD Generation',      val: 'upto 1',     pct: 8   },
      { name: 'JD Uploads',         val: 'upto 1',     pct: 8   },
      { name: 'Resume Parsing',     val: 'upto 10',    pct: 10  },
      { name: 'Candidate Matching', val: 'upto 10',    pct: 10  },
    ],
    included: [
      'AI-powered interview Platform',
      'Automated Personalized questions',
      'Resume parsing & scoring',
      'Candidate match ranking',
      'JD upload & analysis',
      'Dashboard analytics',
    ],
    notIncluded: [
      'Multi-pipeline support',
      'Team collaboration tools',
      'Priority support',
      'Custom integrations',
    ],
    useCases: [
      {
        title: 'Hire your Employees faster',
        desc: 'Run structured AI interviews for every candidate without spending hours on phone screens.',
      },
      {
        title: 'Create a new role quickly',
        desc: 'Generate a JD with AI, upload it, and start matching candidates within minutes.',
      },
      {
        title: 'Freelance recruitment projects',
        desc: 'Handle a single client hiring mandate end-to-end without a full ATS subscription.',
      },
    ],
    upgradeNote:
      'Feeling overwhelmed while taking interviews? The Basic plan gives you 5× capacity for growing startup teams.',
  },
  {
    name: 'Basic',
    badge: 'Startup Ready',
    color: 'blue',
    inr: '₹21,000',
    usd: '$350',
    credits: '21,000',
    tagline: 'For the startup teams looking to automate their hiring process.',
    idealFor: ['Seed-stage Startups', 'Small HR Teams', 'Recruitment Agencies', 'Tech Companies <50 people'],
    teamSize: 6,
    quotas: [
      { name: 'AI Interviews',      val: 'upto 50',  pct: 30 },
      { name: 'JD Generation',      val: 'upto 5',   pct: 25 },
      { name: 'JD Uploads',         val: 'upto 5',   pct: 25 },
      { name: 'Resume Parsing',     val: 'upto 50',  pct: 30 },
      { name: 'Candidate Matching', val: 'upto 50',  pct: 30 },
    ],
    included: [
      'All Starter features',
      'Team collaboration access',
      'Multiple open roles simultaneously',
      'Comparative candidate scoring',
      'Resume batch upload',
      'Hiring funnel overview',
    ],
    notIncluded: [
      'Multi-pipeline workflows',
      'Priority support queue',
      'Custom API integrations',
      'Dedicated account manager',
    ],
    useCases: [
      {
        title: 'Run a hiring sprint',
        desc: 'Fill 3–5 roles in parallel across departments — engineering, sales, and ops — in one focused quarter.',
      },
      {
        title: 'Build your hiring SOP',
        desc: 'Use AI-generated JDs and structured interview kits to create a repeatable hiring playbook.',
      },
      {
        title: 'Collaborate with founders',
        desc: 'Share candidate evaluations and interview summaries across your team without extra tools.',
      },
    ],
    upgradeNote:
      'Need to hire across multiple pipelines simultaneously? Standard unlocks 250 interviews and multi-pipeline support.',
  },
  {
    name: 'Standard',
    badge: 'Most Popular',
    color: 'featured',
    inr: '₹1,00,000',
    usd: '$1,650',
    credits: '1,00,000',
    tagline: 'For growing teams running multiple pipelines.',
    idealFor: ['Series A–B Companies', 'In-house Talent Teams', 'Recruitment Agencies', 'HR SaaS Builders'],
    teamSize: 15,
    quotas: [
      { name: 'AI Interviews',      val: 'upto 250', pct: 65 },
      { name: 'JD Generation',      val: 'upto 25',  pct: 60 },
      { name: 'JD Uploads',         val: 'upto 25',  pct: 60 },
      { name: 'Resume Parsing',     val: 'upto 250', pct: 65 },
      { name: 'Candidate Matching', val: 'upto 250', pct: 65 },
    ],
    included: [
      'All Basic features',
      'Multi-pipeline hiring support',
      'Advanced candidate analytics',
      'Role-based team access',
      'Interview scoring rubrics',
      'Bulk JD management',
      'Hiring velocity metrics',
      'Export reports (CSV / PDF)',
    ],
    notIncluded: [
      'Priority support queue',
      'Dedicated account manager',
      'SLA guarantee',
      'Custom API integrations',
    ],
    useCases: [
      {
        title: 'Scale hiring across departments',
        desc: 'Run parallel pipelines for Engineering, Sales, Marketing, and Ops — all tracked in one place.',
      },
      {
        title: 'Operate as a recruitment agency',
        desc: 'Manage multiple client mandates under one account with separate pipelines per client.',
      },
      {
        title: 'Reduce time-to-hire by 60%',
        desc: 'AI handles screening, ranking, and shortlisting so your team only reviews the top 10% of applicants.',
      },
    ],
    upgradeNote:
      'Hiring at enterprise scale (1,000+ interviews)? Premium adds a dedicated account manager and SLA.',
  },
  {
    name: 'Premium',
    badge: 'Enterprise Grade',
    color: 'green',
    inr: '₹3,75,000',
    usd: '$6,200',
    credits: '3,75,000',
    tagline: 'Tailored for large organisations & agencies.',
    idealFor: ['Enterprise HR Teams', 'Large Staffing Agencies', 'RPO Providers', 'High-Volume Recruiters'],
    teamSize: 25,
    quotas: [
      { name: 'AI Interviews',      val: 'upto 1,000', pct: 100 },
      { name: 'JD Generation',      val: 'upto 100',   pct: 100 },
      { name: 'JD Uploads',         val: 'upto 100',   pct: 100 },
      { name: 'Resume Parsing',     val: 'upto 1,000', pct: 100 },
      { name: 'Candidate Matching', val: 'upto 1,000', pct: 100 },
    ],
    included: [
      'All Standard features',
      'Priority support queue',
      'Dedicated account manager',
      'SLA guarantee',
      'Custom API integrations',
      'White-label options',
      'Advanced role permissions',
      'Audit logs & compliance',
      'Onboarding assistance',
      'Quarterly business reviews',
    ],
    notIncluded: [],
    useCases: [
      {
        title: 'High-volume seasonal hiring',
        desc: 'Ramp up hiring for 200+ roles in a quarter — retail, logistics, or BPO — without adding headcount.',
      },
      {
        title: 'Enterprise-wide talent transformation',
        desc: 'Standardise your hiring across every business unit with a single AI-powered platform and SLA backing.',
      },
      {
        title: 'RPO & staffing at scale',
        desc: 'Run dedicated pipelines for dozens of client companies, with custom integrations into their ATS of choice.',
      },
    ],
    upgradeNote: null,
  },
];

/* ─── Shared micro-components ───────────────────────────────────────────── */
function SectionLabel({ children, green }) {
  return (
    <div className={`pd-section-label ${green ? 'pd-section-label--green' : ''}`}>
      <span className="pd-section-label-bar" />
      {children}
    </div>
  );
}

function Card({ children, featured, greenCard, style: extra = {} }) {
  return (
    <div
      className={`pd-card ${featured ? 'pd-card--featured' : ''} ${greenCard ? 'pd-card--green' : ''}`}
      style={extra}
    >
      {children}
    </div>
  );
}

function QuotaBar({ quota, green }) {
  return (
    <div className="pd-quota">
      <div className="pd-quota-header">
        <span className="pd-quota-name">{quota.name}</span>
        <span className={`pd-quota-val ${green ? 'pd-quota-val--green' : ''}`}>{quota.val}</span>
      </div>
      <div className="pd-quota-track">
        <div
          className={`pd-quota-fill ${green ? 'pd-quota-fill--green' : ''}`}
          style={{ width: `${quota.pct}%` }}
        />
      </div>
    </div>
  );
}

/* ─── Left hero panel ───────────────────────────────────────────────────── */
function HeroPanel({ plan, isUSA, onTrial }) {
  const isGreen    = plan.color === 'green';
  const isFeatured = plan.color === 'featured';

  return (
    <div className="pd-hero-col">
      <Card featured={isFeatured} greenCard={isGreen}>
        <div className={`pd-badge ${isGreen ? 'pd-badge--green' : ''}`}>{plan.badge}</div>

        <h2 className="pd-plan-name">{plan.name}</h2>
        <p className="pd-plan-tagline">{plan.tagline}</p>

        <div className="pd-price-block">
          <div className="pd-price-row-wrap">
            <div className={`pd-price ${isGreen ? 'pd-price--green' : ''}`}>
              {isUSA ? plan.usd : plan.inr}
            </div>
            <span className="pd-intro-badge">INTRO PRICE</span>
          </div>
          <div className="pd-price-note">One-time bundle · usage-based · no expiry</div>
          <div className={`pd-credit-line ${isGreen ? 'pd-credit-line--green' : ''}`}>
            <span className="pd-credit-icon">⚡</span>
            <span>{plan.credits} Credits included</span>
          </div>
        </div>

        <div className="pd-bestfor">
          <div className="pd-bestfor-label">Best for</div>
          <div className="pd-bestfor-tags">
            {plan.idealFor.map(tag => (
              <span key={tag} className={`pd-tag ${isGreen ? 'pd-tag--green' : ''}`}>{tag}</span>
            ))}
          </div>
        </div>

        <button onClick={onTrial} className={`pd-cta ${isGreen ? 'pd-cta--green' : ''}`}>
          Start 1 Month Free Trial
        </button>

        <div className={`pd-glow ${isGreen ? 'pd-glow--green' : ''}`} />
      </Card>

      {plan.upgradeNote && (
        <div className="pd-upgrade-note">
          <span className="pd-upgrade-arrow">→</span>
          {plan.upgradeNote}
        </div>
      )}
    </div>
  );
}

/* ─── Right detail panel ────────────────────────────────────────────────── */
function DetailPanel({ plan }) {
  const isGreen = plan.color === 'green';
  const valColorClass = isGreen ? 'pd-consumption-value--green' : 'pd-consumption-value--blue';

  return (
    <div className="pd-detail-col">
      <Card>
        <SectionLabel green={isGreen}>Usage quotas</SectionLabel>
        {plan.quotas.map(q => <QuotaBar key={q.name} quota={q} green={isGreen} />)}
      </Card>

      {/* RESTORED CREDIT CONSUMPTION TABLE CARD */}
      <Card>
        <SectionLabel green={isGreen}>Credit Consumption</SectionLabel>
        <table className="pd-consumption-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Activity</th>
              <th style={{ textAlign: 'right' }}>Credits</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>JD Generation</td>
              <td className={`pd-consumption-value ${valColorClass}`}>20</td>
            </tr>
            <tr>
              <td>JD Upload</td>
              <td className={`pd-consumption-value ${valColorClass}`}>15</td>
            </tr>
            <tr>
              <td>CV Upload & JD Matching</td>
              <td className={`pd-consumption-value ${valColorClass}`}>15</td>
            </tr>
            <tr>
              <td>Interview</td>
              <td className={`pd-consumption-value ${valColorClass}`}>400</td>
            </tr>
          </tbody>
        </table>
      </Card>

      <Card>
        <SectionLabel green={isGreen}>Common use cases</SectionLabel>
        <div className="pd-usecases">
          {plan.useCases.map((uc, i) => (
            <div key={uc.title} className="pd-usecase">
              <div className={`pd-usecase-num ${isGreen ? 'pd-usecase-num--green' : ''}`}>{i + 1}</div>
              <div>
                <div className="pd-usecase-title">{uc.title}</div>
                <div className="pd-usecase-desc">{uc.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */
export default function PricingDetail() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const [isUSA, setIsUSA] = useState(false);

  const defaultIdx = Math.max(
    0,
    PLANS.findIndex(p => p.name.toLowerCase() === (searchParams.get('plan') || '').toLowerCase()),
  );
  const [activeIdx, setActiveIdx] = useState(defaultIdx);

  // ─── ADD THIS USEEFFECT RIGHT HERE ───
  useEffect(() => {
    window.scrollTo(0, 0);
    const planParam = searchParams.get('plan');
    if (planParam) {
      const targetIdx = PLANS.findIndex(p => p.name.toLowerCase() === planParam.toLowerCase());
      if (targetIdx !== -1) {
        setActiveIdx(targetIdx);
      }
    }
  }, [searchParams]); // Fires every time a navbar item updates the URL query string
  // ──────────────────────────────────────

  const plan    = PLANS[activeIdx];
  const onTrial = useCallback(() => navigate('/tenantRegister'), [navigate]);

  useEffect(() => {
    const id = 'pd-google-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id   = id;
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap';
    document.head.appendChild(link);
  }, []);

  return (
    <div className="pd-root">
      <Navbar/>
      {/* ── Top bar ── */}
      <div className="pd-topbar">
        <div className="pd-topbar-left">
          <a className="pd-topbar-logo" href="/">
            <img src={merubotLogo} alt="MeruBot" style={{ height: '44px', objectFit: 'contain' }} />
          </a>
          <div className="pd-topbar-divider" />
        </div>

        <div className="pd-currency-wrap">
          {[{ label: '₹ India', val: false }, { label: '$ Global', val: true }].map(({ label, val }) => (
            <button
              key={label}
              onClick={() => setIsUSA(val)}
              className={`pd-currency-btn ${isUSA === val ? 'active' : ''}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Plan tab bar ── */}
      {/* <div className="pd-tabbar">
        {PLANS.map((p, i) => (
          <button
            key={p.name}
            className={`pd-tab ${i === activeIdx ? 'active' : ''}`}
            onClick={() => setActiveIdx(i)}
          >
            {p.name}
            {p.badge === 'Most Popular' && <span className="pd-tab-star">★</span>}
          </button>
        ))}
      </div> */}

      {/* ── Page hero ── */}
      <div className="pd-page-hero">
        <div className="pd-pill">Plan Details</div>
        <h1 className="pd-page-title">Everything about <span>each plan</span></h1>
        <p className="pd-page-subtitle">
          Every plan runs on credits buy once, use at your own pace. Explore quotas, features, and real-world use cases for every tier.
        </p>

        <div className="pd-stats-strip">
          {[
            { val: '1 Month',  label: 'Free Trial',         icon: '🎯' },
            { val: 'No',      label: 'Credit Card Needed', icon: '🔒' },
            { val: '4 Tiers', label: 'Plans Available',    icon: '📦' },
          ].map(s => (
            <div key={s.label} className="pd-stat">
              <span className="pd-stat-icon">{s.icon}</span>
              <span className="pd-stat-val">{s.val}</span>
              <span className="pd-stat-label">{s.label}</span>
            </div>
          ))}
        </div>

        <div className="pd-trust-row">
          {[
            { icon: '🛡️', text: 'ISO 27001 Certified' },
            { icon: '🤖', text: 'AI-Powered Engine' },
            { icon: '⚡', text: 'Credit-Based Pricing' },
            { icon: '🌍', text: 'INR & USD Support' },
          ].map(b => (
            <div key={b.text} className="pd-trust-badge">
              <span>{b.icon}</span>
              <span>{b.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div className="pd-layout">
        <HeroPanel plan={plan} isUSA={isUSA} onTrial={onTrial} />
        <DetailPanel plan={plan} />
      </div>

      <footer className="pd-footer">
        © {new Date().getFullYear()} CyberMeru Technologies Pvt. Ltd. · All Rights Reserved.
      </footer>
    </div>
  );
}